package com.schoolconnect.app.repository.support

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.response.TicketListItemResponse
import com.schoolconnect.app.datasource.network.response.TicketResponse
import com.schoolconnect.app.datasource.network.response.SupportTicketStatisticsResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.response.withServerTotalCount
import com.schoolconnect.app.feature.common.PageRequest
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.repository.files.FileUploadRepository
import com.schoolconnect.app.util.scLog
import io.ktor.http.ContentType

data class SupportTicketsDashboard(
    val tickets: List<TicketListItemResponse>,
    val openCount: Int,
    val inProgressCount: Int,
    val resolvedCount: Int
)

object SupportTicketsRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] SupportTicketsRepository"

    suspend fun loadStatistics(): Result<SupportTicketStatisticsResponse> =
        KtorApiClient.getSupportTicketStatistics()

    suspend fun loadPage(
        query: String = "",
        status: String = "",
        periodStart: String? = null,
        periodEnd: String? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sortBy: String = "updated_at",
        order: String = "desc",
        forceRemote: Boolean = false
    ): Result<List<TicketListItemResponse>> = runCatching {
        val request = PageRequest(pageIndex, pageSize)
        val normalizedQuery = query.trim().takeIf { it.isNotBlank() }
        val normalizedStatus = status.trim().takeIf { it.isNotBlank() && it != "All Statuses" }
        if (forceRemote) Unit
        KtorApiClient.getSupportTickets(
            query = normalizedQuery,
            status = normalizedStatus?.uppercase()?.replace(" ", "_"),
            periodStart = periodStart?.trim()?.takeIf { it.isNotBlank() },
            periodEnd = periodEnd?.trim()?.takeIf { it.isNotBlank() },
            limit = request.safePageSize,
            offset = request.offset,
            sortBy = sortBy,
            order = order
        ).getOrThrow().let { items ->
            items.withServerTotalCount(items.serverTotalCountOrNull())
        }
    }.onFailure { error -> scLog("$TAG loadPage failure", error) }

    suspend fun loadDashboard(sortBy: String = "updated_at", order: String = "desc"): Result<SupportTicketsDashboard> = runCatching {
        val tickets = KtorApiClient.getSupportTickets(
            limit = 100,
            offset = 0,
            sortBy = sortBy,
            order = order
        ).getOrThrow()
        // The user-ticket endpoint returns the scoped list for this account. Use that
        // same response for the dashboard cards so a backend that does not apply the
        // optional status filter cannot make every card show the overall total.
        val open = tickets.count { it.status.equals("OPEN", ignoreCase = true) }
        val inProgress = tickets.count { it.status.equals("IN_PROGRESS", ignoreCase = true) }
        val resolved = tickets.count { it.status.equals("RESOLVED", ignoreCase = true) }
        scLog("$TAG loadDashboard complete tickets=${tickets.size} sortBy=$sortBy order=$order open=$open inProgress=$inProgress resolved=$resolved")
        SupportTicketsDashboard(tickets, open, inProgress, resolved)
    }.onFailure { error -> scLog("$TAG loadDashboard failure", error) }

    suspend fun createTicket(
        subject: String,
        description: String,
        attachmentName: String? = null,
        attachmentBytes: ByteArray? = null,
        attachmentContentType: ContentType? = null
    ): Result<Unit> {
        val normalizedSubject = subject.trim()
        val uploadedFileId = if (attachmentName != null && attachmentBytes != null && attachmentContentType != null) {
            FileUploadRepository.uploadStandalone(
                module = "SUPPORT_TICKET",
                filename = attachmentName,
                contentType = attachmentContentType.toString(),
                bytes = attachmentBytes
            ).getOrElse { return Result.failure(it) }
        } else {
            null
        }
        val result = KtorApiClient.createSupportTicket(
            subject = normalizedSubject,
            description = description,
            attachmentFileIds = uploadedFileId?.let(::listOf).orEmpty()
        )
        if (result.isSuccess) return result
        if (!result.isFetchFailure()) {
            uploadedFileId?.let { FileUploadRepository.delete(it) }
            return result
        }

        // The support POST can commit successfully while the browser reports a
        // fetch failure when the response connection is closed unexpectedly.
        // Confirm the mutation through the authoritative user-ticket list before
        // surfacing an error, avoiding duplicate resubmission.
        val confirmed = KtorApiClient.getSupportTickets(
            query = normalizedSubject,
            limit = 100,
            offset = 0,
            sortBy = "created_at",
            order = "desc"
        ).getOrNull()?.any { it.subject?.trim() == normalizedSubject } == true
        if (confirmed) {
            scLog("$TAG createTicket reconciled after fetch failure subject=${normalizedSubject.take(80)}")
            return Result.success(Unit)
        }
        uploadedFileId?.let { FileUploadRepository.delete(it) }
        return result
    }

    suspend fun getTicketDetails(ticketId: String): Result<TicketResponse> =
        KtorApiClient.getSupportTicketDetails(ticketId)

    suspend fun replyToTicket(
        ticketId: String,
        message: String,
        attachmentName: String? = null,
        attachmentBytes: ByteArray? = null,
        attachmentContentType: ContentType? = null
    ): Result<TicketResponse> {
        val uploadedFileId = if (attachmentName != null && attachmentBytes != null && attachmentContentType != null) {
            FileUploadRepository.uploadStandalone(
                module = "SUPPORT_TICKET_REPLY",
                filename = attachmentName,
                contentType = attachmentContentType.toString(),
                bytes = attachmentBytes
            ).getOrElse { return Result.failure(it) }
        } else {
            null
        }
        val result = KtorApiClient.replyToSupportTicket(
            ticketId = ticketId,
            message = message,
            attachmentFileIds = uploadedFileId?.let(::listOf).orEmpty()
        )
        if (result.isFailure) {
            uploadedFileId?.let { FileUploadRepository.delete(it) }
        }
        return result
    }
}

private fun Result<*>.isFetchFailure(): Boolean =
    exceptionOrNull()?.message?.trim()?.equals("Fail to fetch", ignoreCase = true) == true
